import { useState } from 'react';
import { linkedInShareUrl } from '../game/identity';
import { ResultBadge } from './ResultBadge';

interface Props {
  /** Absolute, publicly reachable URL minted by the server, or null. */
  shareUrl: string | null;
  score: number;
  rating: string;
  levelNumber: number;
  levelTitle: string;
  found: number;
  total: number;
  falseAlarms: number;
}

/**
 * Post-game sharing.
 *
 * Three routes, in order of how well they work where the player actually is:
 *  1. The native share sheet on mobile, which offers LinkedIn alongside
 *     everything else the person actually uses.
 *  2. LinkedIn's own share dialog, for desktop.
 *  3. Copying the link, which always works.
 *
 * LinkedIn's endpoint takes a URL and nothing else -- the headline and image
 * come from the Open Graph tags on the shared page, which the server renders.
 * There is no supported way to prefill the post text.
 */
export function ShareResult({
  shareUrl,
  score,
  rating,
  levelNumber,
  levelTitle,
  found,
  total,
  falseAlarms,
}: Props) {
  const [copied, setCopied] = useState(false);

  const canNativeShare = typeof navigator !== 'undefined' && 'share' in navigator;

  const nativeShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.share({
        title: 'DevTool Detective',
        text: `I scored ${score}/100 spotting misconfigurations in “${levelTitle}”. Can you beat it?`,
        url: shareUrl,
      });
    } catch {
      // The player dismissed the sheet. Not an error.
    }
  };

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="share">
      <h2>Share your badge with your friends</h2>

      <ResultBadge
        score={score}
        rating={rating}
        levelNumber={levelNumber}
        levelTitle={levelTitle}
        found={found}
        total={total}
        falseAlarms={falseAlarms}
      />

      {shareUrl ? (
        <>
          <div className="share__actions">
            <a
              className="btn btn--linkedin"
              href={linkedInShareUrl(shareUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Post on LinkedIn
            </a>

            {canNativeShare && (
              <button type="button" className="btn btn--ghost" onClick={nativeShare}>
                Share…
              </button>
            )}

            <button type="button" className="btn btn--ghost" onClick={copy}>
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </div>
          <p className="fineprint">
            The shared page shows your score and rating only — never your name or
            email. Anyone with the link can view it. The image LinkedIn shows on
            the post is currently a generic card rather than this badge.
          </p>
        </>
      ) : (
        <p className="fineprint">
          This badge is yours, but sharing is unavailable — the results service
          could not be reached, so no share link was created. Your score still
          counted.
        </p>
      )}
    </div>
  );
}
